if (!customElements.get('volume-tiers')) {
  class VolumeTiers extends HTMLElement {
    constructor() {
      super();
      this.cards = [];
      this.radios = [];
      this.qtyInput = null;

      // Path C — Loop drive-UI integration
      this.tierToPlanMap = {};
      this.pathCEnabled = false;
      this.formObserver = null;
      this.priceObserver = null;
      this._programmaticClick = false;

      this.handleRadioChange = this.handleRadioChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFormMutation = this.handleFormMutation.bind(this);
      this.handlePriceMutation = this.handlePriceMutation.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this._variantListeners = [];
      this._variants = null;
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.volume-tiers__card'));
      this.radios = Array.from(this.querySelectorAll('.volume-tiers__radio'));
      this.qtyInput = document.getElementById(this.dataset.quantityInput);

      this.hideStandardQuantityStepper();

      this.radios.forEach((radio) => {
        radio.addEventListener('change', this.handleRadioChange);
      });

      this.cards.forEach((card) => {
        card.addEventListener('keydown', this.handleKeydown);
      });

      const checked = this.querySelector('.volume-tiers__radio:checked');
      if (checked) {
        this.applySelection(checked, true);
      } else if (this.radios.length > 0) {
        const fallback = this.radios.find((r) => !r.disabled) || this.radios[0];
        if (fallback) {
          fallback.checked = true;
          this.applySelection(fallback, true);
        }
      }

      // Form observer runs always — needed for live price updates on subscribe toggle,
      // plus Path C plan dispatch when configured.
      this.observeSellingPlanChanges();

      // Price observer — catches variant changes by watching Sense's price element
      this.observePriceChanges();

      // Variant change listener — triggers a recompute when the customer picks a
      // different variant. This is the primary signal; the price observer is a backup.
      this.observeVariantInputs();

      // Path C drive-UI integration (only activates if plan IDs configured)
      this.initLoopIntegration();

      // Initial price sync — in case subscribe is already active on page load.
      // (Liquid pre-renders the Single tier with the default subscribe discount when
      // selling plans are present, so for the common case the values already match
      // and this call is a no-op thanks to idempotent writes.)
      this.handleSubscribeStateChange();
    }

    disconnectedCallback() {
      this.radios.forEach((radio) => radio.removeEventListener('change', this.handleRadioChange));
      this.cards.forEach((card) => card.removeEventListener('keydown', this.handleKeydown));
      if (this.formObserver) {
        this.formObserver.disconnect();
        this.formObserver = null;
      }
      if (this.priceObserver) {
        this.priceObserver.disconnect();
        this.priceObserver = null;
      }
      this._variantListeners.forEach(({ target, type, fn }) => target.removeEventListener(type, fn));
      this._variantListeners = [];
      if (this._readyTimeout) {
        clearTimeout(this._readyTimeout);
        this._readyTimeout = null;
      }
      document.body.classList.remove('volume-tiers-path-c-active');
    }

    hideStandardQuantityStepper() {
      const productInfo = this.closest('product-info') || document;
      const qtyForm = productInfo.querySelector('.product-form__quantity');
      if (qtyForm && !qtyForm.classList.contains('volume-tiers__qty-hidden')) {
        qtyForm.classList.add('volume-tiers__qty-hidden');
      }
    }

    handleRadioChange(event) {
      this.applySelection(event.currentTarget, false);
    }

    applySelection(radio, silent) {
      this.cards.forEach((card) => {
        const cardRadio = card.querySelector('.volume-tiers__radio');
        card.classList.toggle('is-selected', cardRadio === radio);
      });

      if (!this.qtyInput) return;

      const qty = parseInt(radio.value, 10);
      if (Number.isNaN(qty)) return;

      if (this.qtyInput.value !== String(qty)) {
        this.qtyInput.value = qty;

        if (!silent) {
          this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const clamped = parseInt(this.qtyInput.value, 10);
        if (!Number.isNaN(clamped) && clamped !== qty) {
          this.flashClampWarning(radio, clamped);
        }
      }

      // Path C — when tier changes and subscribe is already active,
      // sync Loop's plan to match the new tier.
      if (this.pathCEnabled && !silent) {
        this.syncLoopPlanToTier();
      }

      // Path C — refresh which Loop subscribe rows are visible (3-Pack/6-Pack
      // rows only show when their tier is selected)
      if (this.pathCEnabled) {
        this.updateLoopRowVisibility();
      }

      // Tier change must also re-sync Loop's One Time Purchase row price.
      // The form observer doesn't fire on tier change alone (qty input value
      // is updated as a property, not an attribute), so call it directly.
      this.updateLoopOneTimePrice();
      // Same for the matched subscribe row — show the bundle total, not per-unit.
      this.updateLoopMatchedSubscribeRowPrice();
    }

    flashClampWarning(radio, clampedTo) {
      const card = radio.closest('.volume-tiers__card');
      if (!card) return;
      card.setAttribute('data-clamped-to', String(clampedTo));
      card.classList.add('volume-tiers__card--clamped');
      setTimeout(() => card.classList.remove('volume-tiers__card--clamped'), 1200);
    }

    handleKeydown(event) {
      const key = event.key;
      const isForward = key === 'ArrowRight' || key === 'ArrowDown';
      const isBack = key === 'ArrowLeft' || key === 'ArrowUp';
      if (!isForward && !isBack) return;

      event.preventDefault();
      const currentCard = event.currentTarget;
      const currentIndex = this.cards.indexOf(currentCard);
      if (currentIndex === -1) return;

      const direction = isForward ? 1 : -1;
      const total = this.cards.length;
      const nextIndex = (currentIndex + direction + total) % total;
      const nextCard = this.cards[nextIndex];
      const nextRadio = nextCard.querySelector('.volume-tiers__radio');
      if (!nextRadio || nextRadio.disabled) return;

      nextRadio.checked = true;
      nextRadio.focus();
      this.applySelection(nextRadio, false);
    }

    // ============================================================
    // Path C — Loop drive-UI integration
    // ============================================================
    // When a customer picks a tier + Subscribe, we click the matching
    // Loop selling-plan radio. Loop's existing handlers fire and
    // inject the correct selling_plan_id into the cart-add form.
    //
    // We don't write the selling_plan input directly — Loop overwrites
    // any input we'd add (see loop-widget.js lines 1596-1613).
    // Driving Loop's UI is the only stable integration path.
    //
    // Plan IDs are configured per-block in the theme customizer
    // (volume-tiers block settings → Subscription integration).
    // If unconfigured, Path C silently disables — Loop's default
    // plan stays in effect for subscribers regardless of tier.
    // ============================================================

    initLoopIntegration() {
      // Build qty -> selling_plan_id map from data attributes.
      // Format: data-plan-id-qty-3="1234567890", data-plan-id-qty-6="9876543210"
      // qty 1 has no override — uses Loop's default Subscribe & Save plan.
      this.tierToPlanMap = {};
      this.cards.forEach((card) => {
        const qty = parseInt(card.dataset.qty, 10);
        if (Number.isNaN(qty) || qty <= 1) return;
        const planId = this.dataset[`planIdQty${qty}`];
        if (planId && String(planId).trim() !== '') {
          this.tierToPlanMap[qty] = String(planId).trim();
        }
      });

      if (Object.keys(this.tierToPlanMap).length === 0) {
        // Path C not configured — silently disabled
        this.pathCEnabled = false;
        return;
      }

      this.pathCEnabled = true;
      // eslint-disable-next-line no-console
      console.info('[volume-tiers] Path C drive-UI integration enabled', this.tierToPlanMap);

      // Mark body so Path C CSS rules can target only when active
      document.body.classList.add('volume-tiers-path-c-active');

      // Initial sync — if a tier is already selected and subscribe is already active
      this.syncLoopPlanToTier();

      // Initial row visibility — hide non-matching subscribe rows
      this.updateLoopRowVisibility();
    }

    observeSellingPlanChanges() {
      const form = this.getProductForm();
      if (!form) {
        // eslint-disable-next-line no-console
        console.warn('[volume-tiers] Could not find product form for subscribe state observation. Live price update on subscribe toggle will not work.');
        return;
      }

      this.formObserver = new MutationObserver(this.handleFormMutation);
      this.formObserver.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      });
      // eslint-disable-next-line no-console
      console.info('[volume-tiers] Form observer attached for subscribe state changes.');
    }

    handleFormMutation() {
      // Always update displayed prices on form mutation (subscribe state may have changed)
      this.handleSubscribeStateChange();
      // Re-evaluate Loop row visibility (in case Loop just rendered/re-rendered rows)
      if (this.pathCEnabled) {
        this.updateLoopRowVisibility();
      }
      // Skip Path C plan sync for our own programmatic clicks (avoid loop)
      if (this._programmaticClick) return;
      this.syncLoopPlanToTier();
    }

    syncLoopPlanToTier() {
      if (!this.pathCEnabled) return;

      const tierRadio = this.querySelector('.volume-tiers__radio:checked');
      if (!tierRadio) return;

      const qty = parseInt(tierRadio.value, 10);
      if (Number.isNaN(qty)) return;

      const expectedPlanId = this.tierToPlanMap[qty];

      // Check if subscribe is currently active (Loop has injected a selling_plan input)
      const form = this.getProductForm();
      if (!form) return;
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');

      // If subscribe isn't active, no override needed
      if (!sellingPlanInput || !sellingPlanInput.value) return;

      const currentPlanId = String(sellingPlanInput.value);

      if (expectedPlanId) {
        // Tier 3-Pack/6-Pack: ensure the customer's current selling_plan is in the
        // SAME LOOP GROUP as our configured plan ID — not necessarily the exact same ID.
        // This is critical for multi-frequency plans: e.g. the 3-Pack plan may have
        // three frequencies (every 3/4/6 months), each with its own selling_plan_id.
        // Our configured ID is just one of them. If the customer picked a different
        // frequency in Loop's dropdown, that's a valid choice within the same plan —
        // don't override it. Only override if their current plan is in a different group.
        const groups = this.getLoopSubscriptionGroups();
        const currentGroup = groups.find((g) => g.planIds.includes(currentPlanId));
        const targetGroup = groups.find((g) => g.planIds.includes(expectedPlanId));
        if (currentGroup && currentGroup === targetGroup) return;
        this.clickMatchingLoopPlan(qty);
      } else {
        // qty 1 — Loop's default plan is correct, but if a tier-mapped plan
        // (or any frequency from a tier-mapped group) is still selected from a previous
        // tier choice, switch back to default.
        const tierMappedPlanIds = Object.values(this.tierToPlanMap);
        const groups = this.getLoopSubscriptionGroups();
        const currentGroup = groups.find((g) => g.planIds.includes(currentPlanId));
        const isOnTierMappedGroup = currentGroup && currentGroup.planIds.some((id) => tierMappedPlanIds.includes(id));
        if (isOnTierMappedGroup) {
          this.clickDefaultLoopPlan();
        }
      }
    }

    // Loop's purchase-options widget renders each subscription plan as:
    //   <div class="loop-subscription-group">
    //     <input class="loop-subscription-group-radio" name="loop_purchase_option" />
    //     <select class="loop-selling-plan-selector">
    //       <option value="<selling_plan_id>">...</option>
    //     </select>
    //   </div>
    // The selling_plan_id (the number we get from Loop admin) lives on the OPTION,
    // not the group radio. To match a tier to its plan, we find the group whose
    // <option> values include our plan ID, then click that group's radio.
    getLoopSubscriptionGroups() {
      const groups = [];
      document.querySelectorAll('.loop-subscription-group').forEach((group) => {
        const radio = group.querySelector('.loop-subscription-group-radio');
        const planIds = Array.from(group.querySelectorAll('option'))
          .map((o) => String(o.value || '').trim())
          .filter((v) => v);
        if (radio && planIds.length > 0) {
          groups.push({ group, radio, planIds });
        }
      });
      return groups;
    }

    updateLoopRowVisibility() {
      if (!this.pathCEnabled) return;

      const tierRadio = this.querySelector('.volume-tiers__radio:checked');
      if (!tierRadio) return;
      const qty = parseInt(tierRadio.value, 10);
      if (Number.isNaN(qty)) return;

      const expectedPlanId = this.tierToPlanMap[qty] || null;
      const tierMappedPlanIds = Object.values(this.tierToPlanMap);

      this.getLoopSubscriptionGroups().forEach(({ group, planIds }) => {
        let shouldShow;
        if (expectedPlanId) {
          // 3-Pack / 6-Pack tier: only the group containing the matching plan is visible
          shouldShow = planIds.includes(expectedPlanId);
        } else {
          // Single tier: hide groups that contain tier-mapped plans (3-Pack, 6-Pack)
          shouldShow = !planIds.some((id) => tierMappedPlanIds.includes(id));
        }
        group.classList.toggle('volume-tiers__hidden-loop-row', !shouldShow);
      });
    }

    clickDefaultLoopPlan() {
      const tierMappedPlanIds = Object.values(this.tierToPlanMap);
      const groups = this.getLoopSubscriptionGroups();
      for (const { radio, planIds } of groups) {
        // Skip groups whose plans are tier-mapped — we want the default 1-Pack group
        if (planIds.some((id) => tierMappedPlanIds.includes(id))) continue;
        this._programmaticClick = true;
        radio.click();
        setTimeout(() => { this._programmaticClick = false; }, 150);
        return;
      }
    }

    clickMatchingLoopPlan(qty) {
      const planId = this.tierToPlanMap[qty];
      if (!planId) return;

      // Find the .loop-subscription-group containing an <option> with our planId,
      // then click its group radio.
      const groups = this.getLoopSubscriptionGroups();
      for (const { radio, planIds } of groups) {
        if (planIds.includes(planId)) {
          this._programmaticClick = true;
          radio.click();
          setTimeout(() => { this._programmaticClick = false; }, 150);
          return;
        }
      }

      // eslint-disable-next-line no-console
      console.warn(
        `[volume-tiers] Path C: no Loop subscription group contains plan ID ${planId} (tier qty ${qty}). ` +
        `Likely cause: the plan is not attached to this product in Loop's Products tab, ` +
        `or Loop's DOM structure has changed (expected .loop-subscription-group with an <option value="${planId}">).`
      );
    }

    // ============================================================
    // Live price update — subscribe vs one-time
    // ============================================================
    // Recalculates and rewrites the displayed price on each tier card
    // when the customer toggles One-time / Subscribe.
    //
    // Pricing rules (under Path C):
    //   - Tier 1 (qty 1): one-time = 0% off, subscribe = data-default-subscribe-discount-pct (e.g. 5%)
    //   - Tier 2 (qty 3): one-time = bundle %, subscribe = bundle % (matches via Path C plan)
    //   - Tier 3 (qty 6): one-time = bundle %, subscribe = bundle % (matches via Path C plan)
    //
    // So in practice, only Tier 1 visibly changes when subscribe toggles.
    // Tier 2 and Tier 3 stay the same — their bundle % already covers
    // the subscription scenario via the matched Loop plan.
    // ============================================================

    getProductForm() {
      // Sense versions vary: some wrap in <product-info>, others use
      // <section id="ProductInfo-..."> or .product__info-container.
      // Try several patterns; fall back to document scope.
      const root = this.closest(
        'product-info, [id^="ProductInfo"], .product__info-container, .product__info-wrapper'
      ) || document;
      return root.querySelector('form[action*="/cart/add"]');
    }

    isSubscribeActive() {
      const form = this.getProductForm();
      if (!form) return false;
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
      return !!(sellingPlanInput && sellingPlanInput.value);
    }

    handleSubscribeStateChange() {
      const isSubscribe = this.isSubscribeActive();
      this.cards.forEach((card) => {
        this.updateCardPrices(card, isSubscribe);
      });
      // Loop's "One Time Purchase" row shows per-unit price by default; rewrite it to
      // reflect the tier total (e.g., 3-Pack one-time = 3 units × unit × (1 - bundle %))
      this.updateLoopOneTimePrice();
      // Same problem with the matched subscribe row — Loop renders per-unit price.
      // For 3-Pack / 6-Pack tiers we override to show the bundle total (matches the
      // tier card + the One Time Purchase row for visual consistency).
      this.updateLoopMatchedSubscribeRowPrice();
    }

    updateCardPrices(card, isSubscribe) {
      const qty = parseInt(card.dataset.qty, 10);
      if (Number.isNaN(qty) || qty <= 0) return;

      // Always read the CURRENT variant's price (handles variant switches)
      const unitPrice = this.getCurrentUnitPriceCents();
      if (Number.isNaN(unitPrice) || unitPrice <= 0) return;

      // Determine effective discount %:
      //   - Subscribe + qty 1: use default subscription discount % (parent dataset)
      //   - Subscribe + qty > 1: use the tier's own subscribe rate (data-subscribe-pct),
      //     which differs from the one-time bundle rate (e.g. 3-Pack: 10% one-time, 12.5% subscribe).
      //     Falls back to bundle rate if the tier-specific subscribe rate isn't set.
      //   - One-time: always use the bundle % (data-discount-pct)
      let discountPct;
      if (isSubscribe && qty === 1) {
        // 1-Pack subscribe = Loop's default Subscribe & Save plan %
        // Fallback to 5 if the setting wasn't configured in the customizer
        // (e.g., the block predates the schema setting being added).
        discountPct = parseFloat(this.dataset.defaultSubscribeDiscountPct);
        if (Number.isNaN(discountPct)) discountPct = 5;
      } else if (isSubscribe) {
        // Multi-pack subscribe: prefer the tier's own subscribe rate, fall back to bundle rate.
        discountPct = parseFloat(card.dataset.subscribePct);
        if (Number.isNaN(discountPct) || discountPct <= 0) {
          discountPct = parseFloat(card.dataset.discountPct);
        }
        if (Number.isNaN(discountPct)) discountPct = 0;
      } else {
        // One-time: always the bundle rate.
        discountPct = parseFloat(card.dataset.discountPct);
        if (Number.isNaN(discountPct)) discountPct = 0;
      }

      // Compute prices in cents (Shopify standard)
      const subtotal = unitPrice * qty;
      const final = subtotal - (subtotal * discountPct / 100);

      // Update DOM
      const priceWasEl = card.querySelector('.volume-tiers__price-was');
      const priceNowEl = card.querySelector('.volume-tiers__price-now');
      const priceEachEl = card.querySelector('.volume-tiers__price-each');

      // Idempotent writes — only mutate the DOM if the value actually changes,
      // otherwise the form observer fires on our own writes and we cycle.
      if (priceWasEl) {
        if (discountPct > 0) {
          const text = this.formatMoney(subtotal);
          if (priceWasEl.textContent !== text) priceWasEl.textContent = text;
          if (priceWasEl.style.display === 'none') priceWasEl.style.display = '';
        } else if (priceWasEl.style.display !== 'none') {
          priceWasEl.style.display = 'none';
        }
      }

      if (priceNowEl) {
        const text = this.formatMoney(final);
        if (priceNowEl.textContent !== text) priceNowEl.textContent = text;
      }

      if (priceEachEl && qty > 1) {
        const perUnit = final / qty;
        const text = `${this.formatMoney(perUnit)} each`;
        if (priceEachEl.textContent !== text) priceEachEl.textContent = text;
      }
    }

    loadVariants() {
      if (this._variants !== null) return this._variants;
      const script = this.querySelector('script[data-volume-tiers-variants]');
      if (!script) {
        this._variants = [];
        return this._variants;
      }
      try {
        const parsed = JSON.parse(script.textContent);
        this._variants = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        this._variants = [];
      }
      return this._variants;
    }

    findCurrentVariantPriceCents() {
      const form = this.getProductForm();
      const variantInput = form && form.querySelector('input[name="id"]');
      const variantId = variantInput && variantInput.value;
      if (!variantId) return null;

      const variants = this.loadVariants();
      const v = variants.find((x) => String(x.id) === String(variantId));
      return v && typeof v.price === 'number' ? v.price : null;
    }

    getCurrentUnitPriceCents() {
      // Strategy 1: look up by current variant ID in the embedded variants JSON.
      // Most reliable — independent of DOM mutations or Sense version.
      const fromJson = this.findCurrentVariantPriceCents();
      if (fromJson !== null && fromJson > 0) return fromJson;

      // Strategy 2: parse Sense's main price element (works on the storefront where
      // the price element re-renders on variant change).
      const root = this.closest('[id^="ProductInfo"], product-info, .product__info-container') || document;
      const priceEl = root.querySelector(
        '.price__sale .price-item--sale, .price-item--sale, .price__regular .price-item--regular, .price-item--regular'
      );
      if (priceEl) {
        const text = priceEl.textContent.trim();
        const match = text.match(/[\d,]+\.\d{1,2}|[\d,]+/);
        if (match) {
          const dollars = parseFloat(match[0].replace(/,/g, ''));
          if (!Number.isNaN(dollars) && dollars > 0) return Math.round(dollars * 100);
        }
      }

      // Strategy 3: cached initial value from Liquid render
      const cached = parseFloat(this.dataset.unitPrice);
      return Number.isNaN(cached) ? 0 : cached;
    }

    observeVariantInputs() {
      // Variant changes don't always trigger our form mutation observer (the variant
      // input value is set as a property, not an attribute, and Sense's price element
      // lives outside the cart-add form). Listen for change events directly.
      const form = this.getProductForm();
      if (form) {
        form.addEventListener('change', this.handleVariantChange);
        this._variantListeners.push({ target: form, type: 'change', fn: this.handleVariantChange });
      }
      const root = this.closest('[id^="ProductInfo"], product-info, .product__info-container') || document;
      const variantContainer = root.querySelector('variant-selects, variant-radios');
      if (variantContainer && variantContainer !== form) {
        variantContainer.addEventListener('change', this.handleVariantChange);
        this._variantListeners.push({ target: variantContainer, type: 'change', fn: this.handleVariantChange });
      }
    }

    handleVariantChange(event) {
      // Filter to variant-related inputs only (skip our own tier radios, qty stepper, etc.)
      const target = event && event.target;
      if (target) {
        const name = target.name || '';
        const isVariantInput = name === 'id' || name.indexOf('options[') === 0;
        const isInsideVariantContainer = target.closest && target.closest('variant-selects, variant-radios');
        if (!isVariantInput && !isInsideVariantContainer) return;
      }
      // Small delay to let Sense's variant-change DOM updates settle before we recompute.
      setTimeout(() => {
        this.handleSubscribeStateChange();
        this.swapTierImagesForCurrentVariant();
      }, 50);
    }

    swapTierImagesForCurrentVariant() {
      // Replace each tier card's <img> src/srcset with the current variant's image.
      // Variant-specific images come from variant metafields; falls back to product
      // metafield or block setting (all handled in the Liquid that built the JSON).
      const form = this.getProductForm();
      const variantInput = form && form.querySelector('input[name="id"]');
      const variantId = variantInput && variantInput.value;
      if (!variantId) return;

      const variants = this.loadVariants();
      const v = variants.find((x) => String(x.id) === String(variantId));
      if (!v) return;

      this.cards.forEach((card) => {
        const qty = parseInt(card.dataset.qty, 10);
        if (Number.isNaN(qty) || qty <= 0) return;

        // Cards are rendered for tier 1, 2, 3 corresponding to data-qty values
        // configured in block settings. Map qty → tier index by their on-page order.
        const tierIndex = this.cards.indexOf(card) + 1;
        const newUrl = v[`tier_${tierIndex}_image`];
        if (!newUrl) return;

        const img = card.querySelector('.volume-tiers__img');
        if (!img || img.tagName !== 'IMG') return;

        if (img.src !== newUrl) {
          // Clear srcset so the browser picks our new URL, not a cached one
          img.removeAttribute('srcset');
          img.src = newUrl;
        }
      });
    }

    observePriceChanges() {
      const root = this.closest('[id^="ProductInfo"], product-info, .product__info-container') || document;
      const priceWrapper = root.querySelector('.price, .price__container');
      if (!priceWrapper) return;
      this.priceObserver = new MutationObserver(this.handlePriceMutation);
      this.priceObserver.observe(priceWrapper, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    handlePriceMutation() {
      // Variant change updated the displayed price — re-render tier cards
      this.handleSubscribeStateChange();
    }

    updateLoopMatchedSubscribeRowPrice() {
      // Loop renders the subscribe-row price as the per-unit subscribed price.
      // For 3-Pack / 6-Pack tiers that's misleading next to the tier card's total +
      // the One Time Purchase row's total. Override the visible (tier-matched) group's
      // price element with the bundle total at the SUBSCRIBE rate (not bundle rate).
      const tierRadio = this.querySelector('.volume-tiers__radio:checked');
      if (!tierRadio) return;
      const qty = parseInt(tierRadio.value, 10);
      if (Number.isNaN(qty) || qty <= 1) return; // Single tier: per-unit = total, no override needed

      const expectedPlanId = this.tierToPlanMap[qty];
      if (!expectedPlanId) return; // Plan ID not configured — can't identify the matched group

      const card = tierRadio.closest('.volume-tiers__card');
      if (!card) return;
      // Use the tier's subscribe rate (e.g. 12.5% for 3-Pack subscribe), falling back to
      // bundle rate if the subscribe-specific rate isn't configured.
      let discountPct = parseFloat(card.dataset.subscribePct);
      if (Number.isNaN(discountPct) || discountPct <= 0) {
        discountPct = parseFloat(card.dataset.discountPct);
      }
      if (Number.isNaN(discountPct)) discountPct = 0;

      const unitPrice = this.getCurrentUnitPriceCents();
      if (Number.isNaN(unitPrice) || unitPrice <= 0) return;

      const subtotal = unitPrice * qty;
      const final = subtotal - (subtotal * discountPct / 100);

      const groups = this.getLoopSubscriptionGroups();
      const matchedGroup = groups.find((g) => g.planIds.includes(expectedPlanId));
      if (!matchedGroup) return;

      const priceEl = matchedGroup.group.querySelector('.loop-subscription-group-price-amount');
      if (priceEl) {
        const text = this.formatMoney(final);
        if (priceEl.textContent !== text) priceEl.textContent = text;
      }

      const originalEl = matchedGroup.group.querySelector('.loop-subscription-group-price-amount-original');
      if (originalEl) {
        if (discountPct > 0) {
          const text = this.formatMoney(subtotal);
          if (originalEl.textContent !== text) originalEl.textContent = text;
          if (originalEl.classList.contains('loop-display-none')) {
            originalEl.classList.remove('loop-display-none');
          }
        } else if (!originalEl.classList.contains('loop-display-none')) {
          originalEl.classList.add('loop-display-none');
        }
      }
    }

    updateLoopOneTimePrice() {
      // Update Loop's "One Time Purchase" row price to reflect the selected tier total.
      // Path C consistency: 3-Pack one-time = 3 units × unit × 0.90 (10% via Shopify discount).
      // 6-Pack one-time = 6 units × unit × 0.85 (15% via Shopify discount).
      // Single = 1 × unit (no discount).
      const tierRadio = this.querySelector('.volume-tiers__radio:checked');
      if (!tierRadio) return;
      const qty = parseInt(tierRadio.value, 10);
      if (Number.isNaN(qty)) return;

      const card = tierRadio.closest('.volume-tiers__card');
      if (!card) return;
      const discountPct = parseFloat(card.dataset.discountPct) || 0;

      const unitPrice = this.getCurrentUnitPriceCents();
      if (Number.isNaN(unitPrice) || unitPrice <= 0) return;

      const subtotal = unitPrice * qty;
      const final = subtotal - (subtotal * discountPct / 100);

      // Idempotent writes — Loop's price element is inside the cart-add form subtree,
      // so writing textContent triggers our own form observer. Skip the write when the
      // value already matches to avoid an observer feedback cycle (especially in the
      // theme customizer iframe, which is reentrancy-sensitive).
      const priceEl = document.querySelector('.loop-one-time-purchase-option-price-amount');
      if (priceEl) {
        const text = this.formatMoney(final);
        if (priceEl.textContent !== text) priceEl.textContent = text;
      }

      const originalEl = document.querySelector('.loop-one-time-purchase-option-price-amount-original');
      if (originalEl) {
        if (discountPct > 0 && qty > 1) {
          const text = this.formatMoney(subtotal);
          if (originalEl.textContent !== text) originalEl.textContent = text;
          if (originalEl.classList.contains('loop-display-none')) {
            originalEl.classList.remove('loop-display-none');
          }
        } else if (!originalEl.classList.contains('loop-display-none')) {
          originalEl.classList.add('loop-display-none');
        }
      }
    }

    formatMoney(cents) {
      // Use Shopify's money formatter if available (respects shop currency + format settings)
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try {
          return window.Shopify.formatMoney(cents);
        } catch (e) {
          // fall through to default
        }
      }
      // Default fallback: USD-style, 2 decimal places
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
  }

  customElements.define('volume-tiers', VolumeTiers);
}
